/**
 * LeadFormPage — branded Request Information / Schedule Demo flows.
 * NTN Verify Hub is not self-service; public CTAs route here, never to /register.
 *
 * Uses one component for both surfaces, parameterized by `kind`:
 *   - "info" → Request Information
 *   - "demo" → Schedule a Demo
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, ShieldAlert, ArrowRight, Inbox, Briefcase, FileCheck, MessageSquare, ListChecks } from "lucide-react";
import { PIPELINE_STAGES, PIPELINE_DOT } from "@/lib/verifyHub";
import { toast } from "sonner";

const VALUE_POINTS = [
  "Employment Verification", "Landlord Verification", "Document Validation",
  "Risk Indicators", "Decision-Ready Applications", "Workflow Visibility",
];

export default function LeadFormPage({ kind = "info" }) {
  const isDemo = kind === "demo";
  const isPricing = kind === "pricing";
  const kindLabel = isDemo ? "Schedule a Demo" : isPricing ? "Request Pricing" : "Request Information";
  const headline = isDemo
    ? "See NTN Verify Hub in action."
    : isPricing
      ? "Get NTN Verify Hub pricing."
      : "Learn how NTN Verify Hub can help your team.";
  const subhead = isDemo
    ? "Schedule a personalized demonstration and see how NTN Verify Hub moves applications from submission to decision-ready status."
    : isPricing
      ? "Tell us about your portfolio and how you screen today — we'll send a tailored pricing proposal designed for your volume."
      : "Discover how Applicant Verification, Risk Intelligence, and Workflow Management help property managers receive verified, decision-ready applications.";
  const submitLabel = isDemo ? "Schedule my demo" : isPricing ? "Request pricing" : "Request information";
  const formTitle = isDemo ? "Schedule a Demo" : isPricing ? "Request Pricing" : "Request Information";
  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", email: "", phone: "",
    units_managed: "", message: "",
    preferred_contact: "Email", preferred_meeting_time: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, { kind, ...form });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit — try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-xl text-center" data-testid="lead-thank-you">
          <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-7 w-7 text-emerald-700" />
          </div>
          <h1 className="font-serif text-4xl tracking-tight text-slate-900 mt-6">Thank you.</h1>
          <p className="text-slate-700 mt-4 leading-relaxed">
            A member of the NTN team will contact you shortly to discuss your screening,
            verification, and applicant processing needs.
          </p>
          <Link to="/" className="inline-flex items-center gap-1.5 text-slate-900 font-semibold mt-8 hover:underline" data-testid="lead-back-home">
            Back to home <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900" data-testid="lead-signin-link">Client sign in</Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="text-[10px] tracking-widest uppercase font-semibold text-slate-500" data-testid="lead-eyebrow">
            NTN Verify Hub · {kindLabel}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-slate-900 mt-3" data-testid="lead-headline">
            {headline}
          </h1>
          <p className="text-slate-700 mt-5 text-lg leading-relaxed">
            {subhead}
          </p>

          {/* Value points */}
          <ul className="grid grid-cols-2 gap-x-6 gap-y-2 mt-7">
            {VALUE_POINTS.map((v) => (
              <li key={v} className="flex items-center gap-2 text-sm text-slate-800">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> {v}
              </li>
            ))}
          </ul>

          {/* Workflow pipeline (demo only) */}
          {isDemo && (
            <div className="mt-10 bg-white rounded-xl ring-1 ring-slate-200 p-5" data-testid="lead-workflow">
              <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">The NTN Verify Hub workflow</div>
              <ol className="flex items-center justify-between gap-2 mt-4 flex-wrap">
                {PIPELINE_STAGES.map((s, idx) => {
                  const Icon = s.icon;
                  return (
                    <li key={s.key} className="flex-1 min-w-[110px] flex flex-col items-center text-center">
                      <div className={`h-8 w-8 rounded-full ${PIPELINE_DOT[s.tone]} text-white flex items-center justify-center`}>
                        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                      </div>
                      <div className="text-[10px] text-slate-700 mt-1.5 leading-tight">{s.label}</div>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* Risk Intelligence preview */}
          <div className="mt-10 bg-white rounded-xl ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid="lead-risk-preview">
            <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-full bg-amber-100 ring-1 ring-amber-200 flex items-center justify-center">
                <ShieldAlert className="h-3.5 w-3.5 text-amber-700" />
              </div>
              <div>
                <div className="text-[10px] tracking-widest uppercase text-amber-700 font-semibold">Risk Intelligence Summary</div>
                <div className="font-serif text-sm text-slate-900">See what traditional screening misses</div>
              </div>
            </div>
            <ul className="p-4 space-y-2 text-sm">
              {[
                { t: "Income discrepancy identified", s: "Open" },
                { t: "Employment dates differ between sources", s: "Under Review" },
                { t: "Unable to verify prior residency", s: "Open" },
              ].map((r, i) => (
                <li key={i} className="flex items-center justify-between bg-white ring-1 ring-slate-200 rounded-md px-3 py-2">
                  <span className="text-slate-800">{r.t}</span>
                  <span className={`text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border
                    ${r.s === "Open" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                    {r.s}
                  </span>
                </li>
              ))}
            </ul>
            <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs">
              <span className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold mr-2">NTN Recommendation</span>
              <span className="inline-block bg-amber-500 text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold mr-1.5">Review</span>
              <span className="text-slate-800">Additional review recommended.</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-5">
          <form onSubmit={submit} className="bg-white rounded-xl ring-1 ring-slate-200 shadow-md p-6 lg:p-8 lg:sticky lg:top-8" data-testid="lead-form">
            <div className="text-[10px] tracking-widest uppercase font-bold text-slate-600">{formTitle}</div>
            <div className="font-serif text-2xl text-slate-900 mt-1">Talk to NTN</div>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <Field label="First name"><Input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-10 rounded-md" data-testid="lead-first" /></Field>
              <Field label="Last name"><Input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-10 rounded-md" data-testid="lead-last" /></Field>
              <Field label="Company" className="col-span-2"><Input required value={form.company} onChange={(e) => set("company", e.target.value)} className="h-10 rounded-md" data-testid="lead-company" /></Field>
              <Field label="Email" className="col-span-2"><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-10 rounded-md" data-testid="lead-email" /></Field>
              <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-10 rounded-md" data-testid="lead-phone" /></Field>
              <Field label="Units managed">
                <Select value={form.units_managed} onValueChange={(v) => set("units_managed", v)}>
                  <SelectTrigger className="h-10 rounded-md" data-testid="lead-units"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {["1–50", "51–200", "201–500", "501–1,000", "1,000+"].map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {isDemo && (
                <>
                  <Field label="Preferred contact">
                    <Select value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)}>
                      <SelectTrigger className="h-10 rounded-md" data-testid="lead-preferred-contact"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Email", "Phone", "Either"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Preferred meeting time">
                    <Input value={form.preferred_meeting_time} onChange={(e) => set("preferred_meeting_time", e.target.value)}
                           placeholder="e.g. Weekday mornings PT" className="h-10 rounded-md" data-testid="lead-preferred-time" />
                  </Field>
                </>
              )}
              <Field label={isDemo ? "Additional comments" : isPricing ? "Tell us about your needs" : "Message"} className="col-span-2">
                <Textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)}
                          placeholder={isDemo ? "Anything you'd like us to cover during the demo?" : isPricing ? "Current screening volume, services you need, timeline, etc." : "Tell us about your team and what you're looking to solve."}
                          className="rounded-md text-sm" data-testid="lead-message" />
              </Field>
            </div>
            <Button type="submit" disabled={busy} className="w-full mt-5 bg-slate-900 hover:bg-slate-800 h-11 rounded-md" data-testid="lead-submit">
              {busy ? "Submitting…" : submitLabel} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <p className="text-[11px] text-slate-500 text-center mt-3">
              By submitting, you agree to be contacted by NTN about NTN Verify Hub.
            </p>
            <div className="text-xs text-slate-600 text-center mt-4 pt-4 border-t border-slate-100 space-x-3">
              {!isDemo && <Link to="/schedule-demo" className="text-slate-900 hover:underline font-semibold" data-testid="lead-switch-demo">Schedule a demo</Link>}
              {!isPricing && <Link to="/request-pricing" className="text-slate-900 hover:underline font-semibold" data-testid="lead-switch-pricing">Request pricing</Link>}
              {kind !== "info" && <Link to="/request-information" className="text-slate-900 hover:underline font-semibold" data-testid="lead-switch-info">Request information</Link>}
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children, className }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
